import { HeldBenefitView } from '@/services/myBenefits'

/** The benefit TYPE keys a member's claimable entitlements cover, deduplicated. */
const heldTypes = (claimable: HeldBenefitView[]): Set<string> => {
  const held = new Set<string>()
  claimable.forEach((benefit) =>
    (benefit.claimPolicies ?? []).forEach((policy) => {
      const type = (policy as { type?: string | null }).type
      if (type) held.add(type)
    })
  )
  return held
}

/**
 * Whether a participating club takes nothing this member is holding.
 *
 * Matching is by TYPE: a club's search document carries the type keys its offers measure in
 * (`benefitTypes`), and a held entitlement covers types — the same closed vocabulary, so an
 * intersection is the whole test. Nothing here names bundles or benefits.
 *
 * Answers only the negative direction, because only the negative is knowable here: a targeting
 * rule can still turn the member away, so a false answer means "not ruled out", never "this will
 * work" — which is why the surfaces that use it stay neutral when it says false and only dim when
 * it says true. A member holding nothing, or entitlements that carry no types yet, dims nothing:
 * missing data must stay neutral rather than read as a verdict.
 *
 * Mirrors `clubBenefitEligibility.ts` in the mobile app; the two should stay in step.
 */
export const isIneligibleAtClub = (offeredTypeKeys: string[], claimable: HeldBenefitView[]): boolean => {
  if (offeredTypeKeys.length === 0) return false
  if (claimable.length === 0) return false

  const held = heldTypes(claimable)
  if (held.size === 0) return false
  return !offeredTypeKeys.some((type) => held.has(type))
}

/**
 * The same question about ONE program rather than a whole club. A program's types are its terms'
 * keys — the same vocabulary the club document summarizes. A true answer means the member is not
 * holding this KIND of thing, not that the club would turn them away.
 */
export const isIneligibleForProgram = (programTermKeys: string[], claimable: HeldBenefitView[]): boolean =>
  isIneligibleAtClub(programTermKeys, claimable)
